import { useEffect, useMemo, useState } from 'react';
import {
  deleteMail,
  downloadAuthenticated,
  formatBytes,
  formatDate,
  getMail,
  type MailDetail,
} from '../api/client';
import { buildSafeSrcDoc } from '../lib/sanitize';

type Tab = 'html' | 'text' | 'headers' | 'attachments';

interface Props {
  mailId: string | null;
  onDeleted?: (id: string) => void;
}

export default function MailDetailView({ mailId, onDeleted }: Props) {
  const [mail, setMail] = useState<MailDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('html');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!mailId) {
      setMail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setTab('html');
    getMail(mailId)
      .then((data) => {
        if (!cancelled) {
          setMail(data);
          if (!data.html && data.text) setTab('text');
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mailId]);

  const srcDoc = useMemo(() => {
    if (!mail?.html) return '';
    return buildSafeSrcDoc(mail.html);
  }, [mail?.html]);

  if (!mailId) {
    return (
      <div className="panel detail">
        <div className="empty">选择一封邮件查看详情</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="panel detail">
        <div className="empty">加载中…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel detail">
        <div className="error-banner" style={{ margin: 16 }}>
          {error}
        </div>
      </div>
    );
  }

  if (!mail) return null;

  async function onDownloadEml() {
    if (!mail) return;
    setBusy(true);
    try {
      await downloadAuthenticated(`/api/raw/${encodeURIComponent(mail.id)}`, `${mail.id}.eml`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onDownloadAtt(index: number, filename: string) {
    if (!mail) return;
    setBusy(true);
    try {
      await downloadAuthenticated(
        `/api/mail/${encodeURIComponent(mail.id)}/attachments/${index}`,
        filename,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!mail) return;
    if (!confirm('确认删除该邮件？将同时删除 D1 索引与 R2 原始文件。')) return;
    setBusy(true);
    try {
      await deleteMail(mail.id);
      onDeleted?.(mail.id);
      setMail(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const headerEntries = Object.entries(mail.headers || {});

  return (
    <div className="panel detail">
      <div className="detail-header">
        <h1>{mail.subject || '(no subject)'}</h1>
        <div className="muted" style={{ fontSize: 13, display: 'grid', gap: 4 }}>
          <div>
            <strong>From:</strong> {mail.from}
          </div>
          <div>
            <strong>To:</strong> {mail.to}
          </div>
          <div>
            <strong>Mailbox:</strong> {mail.mailbox}
          </div>
          <div>
            <strong>Received:</strong> {formatDate(mail.receivedAt)} · {formatBytes(mail.size)}
          </div>
        </div>
        <div className="detail-actions">
          <button type="button" className="btn" disabled={busy} onClick={onDownloadEml}>
            下载 .eml
          </button>
          <button type="button" className="btn btn-danger" disabled={busy} onClick={onDelete}>
            删除
          </button>
        </div>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab ${tab === 'html' ? 'active' : ''}`}
          onClick={() => setTab('html')}
          disabled={!mail.html}
        >
          HTML
        </button>
        <button
          type="button"
          className={`tab ${tab === 'text' ? 'active' : ''}`}
          onClick={() => setTab('text')}
        >
          纯文本
        </button>
        <button
          type="button"
          className={`tab ${tab === 'headers' ? 'active' : ''}`}
          onClick={() => setTab('headers')}
        >
          Headers
        </button>
        <button
          type="button"
          className={`tab ${tab === 'attachments' ? 'active' : ''}`}
          onClick={() => setTab('attachments')}
        >
          附件 ({mail.attachments?.length || 0})
        </button>
      </div>

      <div className="detail-body">
        {tab === 'html' &&
          (mail.html ? (
            <iframe
              className="html-frame"
              title="mail-html"
              sandbox=""
              referrerPolicy="no-referrer"
              srcDoc={srcDoc}
            />
          ) : (
            <div className="muted">无 HTML 正文</div>
          ))}

        {tab === 'text' && (
          <pre className="pre-block">{mail.text || '(无纯文本正文)'}</pre>
        )}

        {tab === 'headers' && (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 180 }}>Header</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {headerEntries.map(([k, v]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td>
                    <pre className="pre-block" style={{ fontSize: 12 }}>
                      {v}
                    </pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'attachments' &&
          (mail.attachments?.length ? (
            <table className="table">
              <thead>
                <tr>
                  <th>文件名</th>
                  <th>类型</th>
                  <th>大小</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {mail.attachments.map((a) => (
                  <tr key={a.index}>
                    <td>{a.filename}</td>
                    <td>{a.mimeType}</td>
                    <td>{formatBytes(a.size)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn"
                        disabled={busy}
                        onClick={() => onDownloadAtt(a.index, a.filename)}
                      >
                        下载
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="muted">无附件</div>
          ))}
      </div>
    </div>
  );
}
